import { useEffect, useRef } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import SpiralVeyorCore from './SpiralVeyorCore';
import CalcHexFromRal from '../function/CalcHexFromRAL';
import AllProduct from './AllProducts';
import CubicSpaceAvailable from './view/CubicSpaceAvailable';
import SizeCheckUpperEnd from './view/SizeCheckUpperEnd';
import SizeCheckWindings from './view/SizeCheckWindings';
import SizeCheckLowerEnd from './view/SizeCheckLowerEnd';
import HeightDifference from './view/HeightDifference';
import UpperEndView from './view/UpperEndView';
import LowerEndView from './view/LowerEndView';
import { checkProductReady } from '../function/CheckProductReady';
import { useNavigation } from '../function/navigation';

function SpiralVeyor({ inputs, result, drawingBaseDimensions }) {
  const { nav } = useNavigation();
  const height = 5;
  const convRatio =
    height /
    Math.max(
      drawingBaseDimensions.h3,
      2 * drawingBaseDimensions.u1,
      2 * drawingBaseDimensions.l1
    );
  const color = CalcHexFromRal(inputs.frame_colour).background;
  const product = inputs.product_tbh?.[nav.focusedProduct - 1];

  const showAllProduct =
    nav.spiralView === 'home' && product && checkProductReady(product);
  const showCubic = nav.spiralView === 'product' && !nav.focusedProduct;
  const showProductSize =
    nav.spiralView === 'product' && nav.focusedProduct > 0;

  const controlsRef = useRef(null);
  const cameraMode =
    nav.spiralView === 'product'
      ? `${nav.spiralView}_${nav.productSizeView}`
      : nav.spiralView;

  return (
    <Canvas camera={{ position: [10, 10, 10], fov: 50 }}>
      <CameraPreset
        mode={cameraMode}
        controlsRef={controlsRef}
        inputs={inputs}
        result={result}
        convRatio={convRatio}
        drawingBaseDimensions={drawingBaseDimensions}
      />
      <SpiralVeyorCore
        inputs={inputs}
        result={result}
        convRatio={convRatio}
        color={color}
        drawingBaseDimensions={drawingBaseDimensions}
      />
      {nav.currentPage === 'Base machine' &&
        nav.spiralView === 'home' &&
        nav.mainPage === 'machine' && (
          <HeightDifference
            inputs={inputs}
            result={result}
            convRatio={convRatio}
            drawingBaseDimensions={drawingBaseDimensions}
          />
        )}
      {nav.spiralView === 'upper-end' && (
        <UpperEndView
          result={result}
          convRatio={convRatio}
          drawingBaseDimensions={drawingBaseDimensions}
          unitSystem={inputs.unit_system}
        />
      )}
      {nav.spiralView === 'lower-end' && (
        <LowerEndView
          result={result}
          convRatio={convRatio}
          drawingBaseDimensions={drawingBaseDimensions}
          unitSystem={inputs.unit_system}
        />
      )}
      {showAllProduct && (
        <AllProduct
          result={result}
          convRatio={convRatio}
          product={product}
          drawingBaseDimensions={drawingBaseDimensions}
          pause={false}
        />
      )}
      {showCubic && (
        <CubicSpaceAvailable
          result={result}
          convRatio={convRatio}
          drawingBaseDimensions={drawingBaseDimensions}
          unitSystem={inputs.unit_system}
        />
      )}
      {showProductSize && (
        <>
          <SizeCheckUpperEnd
            inputs={inputs}
            result={result}
            convRatio={convRatio}
            drawingBaseDimensions={drawingBaseDimensions}
          />
          <SizeCheckWindings
            inputs={inputs}
            result={result}
            convRatio={convRatio}
            drawingBaseDimensions={drawingBaseDimensions}
          />
          <SizeCheckLowerEnd
            inputs={inputs}
            result={result}
            convRatio={convRatio}
            drawingBaseDimensions={drawingBaseDimensions}
          />
        </>
      )}
      <OrbitControls ref={controlsRef} target={[0, 2.5, 0]} />
    </Canvas>
  );
}

export default SpiralVeyor;

function CameraPreset({
  mode,
  controlsRef,
  inputs,
  result,
  convRatio,
  drawingBaseDimensions,
}) {
  const { camera } = useThree();

  useEffect(() => {
    const reverseConfig = ['A', 'C', 'E', 'G', 'L'].includes(
      drawingBaseDimensions.configuration
    )
      ? 1
      : -1;

    const configAng =
      reverseConfig === 1
        ? drawingBaseDimensions.configuration_angle
        : 360 - drawingBaseDimensions.configuration_angle;

    const cameraConfigs = {
      home: {
        target: new THREE.Vector3(0, 2.5, 0),
        distance: 18,
        polar: THREE.MathUtils.degToRad(80),
        azimuth: THREE.MathUtils.degToRad(30),
      },
      'upper-end': {
        target: new THREE.Vector3(
          ((Math.cos(THREE.MathUtils.degToRad(configAng)) *
            drawingBaseDimensions.central_diam) /
            2) *
            convRatio,
          convRatio * drawingBaseDimensions.h3,
          ((Math.sin(THREE.MathUtils.degToRad(configAng)) *
            drawingBaseDimensions.central_diam) /
            2) *
            convRatio
        ),
        distance: 36 / drawingBaseDimensions.nbr_windings,
        polar: THREE.MathUtils.degToRad(90),
        azimuth: THREE.MathUtils.degToRad(configAng),
      },
      'lower-end': {
        target: new THREE.Vector3(
          (-(reverseConfig * drawingBaseDimensions.l1) / 2) * convRatio,
          convRatio * drawingBaseDimensions.h1,
          (-(reverseConfig * drawingBaseDimensions.central_diam) / 2) *
            convRatio
        ),
        distance: 36 / drawingBaseDimensions.nbr_windings,
        polar: THREE.MathUtils.degToRad(90),
        azimuth: THREE.MathUtils.degToRad(0),
      },
      product_windings: {
        target: new THREE.Vector3(
          ((Math.cos(THREE.MathUtils.degToRad(configAng + 30 * reverseConfig)) *
            drawingBaseDimensions.central_diam) /
            2) *
            convRatio,
          convRatio * (drawingBaseDimensions.h3 - result.pitch / 2),
          ((Math.sin(THREE.MathUtils.degToRad(configAng + 30 * reverseConfig)) *
            drawingBaseDimensions.central_diam) /
            2) *
            convRatio
        ),
        distance: 24 / drawingBaseDimensions.nbr_windings,
        polar: THREE.MathUtils.degToRad(95),
        azimuth: THREE.MathUtils.degToRad(configAng - 45 * reverseConfig),
      },
      'product_upper-end': {
        target: new THREE.Vector3(
          ((Math.cos(THREE.MathUtils.degToRad(configAng)) *
            drawingBaseDimensions.central_diam) /
            2) *
            convRatio,
          convRatio * drawingBaseDimensions.h3,
          ((Math.sin(THREE.MathUtils.degToRad(configAng)) *
            drawingBaseDimensions.central_diam) /
            2) *
            convRatio
        ),
        distance: 36 / drawingBaseDimensions.nbr_windings,
        polar: THREE.MathUtils.degToRad(80),
        azimuth: THREE.MathUtils.degToRad(configAng + 100 * reverseConfig),
      },
      'product_lower-end': {
        target: new THREE.Vector3(
          (drawingBaseDimensions.central_diam / 2) * convRatio,
          convRatio * (drawingBaseDimensions.h1 + result.pitch / 2),
          0
        ),
        distance: 36 / drawingBaseDimensions.nbr_windings,
        polar: THREE.MathUtils.degToRad(90),
        azimuth: THREE.MathUtils.degToRad(-100 * reverseConfig),
      },
      default: {
        target: new THREE.Vector3(0, 2.5, 0),
        distance: 18,
        polar: THREE.MathUtils.degToRad(80),
        azimuth: THREE.MathUtils.degToRad(30),
      },
    };

    const config = cameraConfigs[mode] || cameraConfigs.default;

    const x =
      config.target.x +
      config.distance * Math.sin(config.polar) * Math.cos(config.azimuth);
    const y = config.target.y + config.distance * Math.cos(config.polar);
    const z =
      config.target.z +
      config.distance * Math.sin(config.polar) * Math.sin(config.azimuth);

    camera.position.set(x, y, z);
    camera.lookAt(config.target);

    camera.updateMatrixWorld();
    camera.updateProjectionMatrix();

    if (controlsRef.current) {
      controlsRef.current.target.copy(config.target);
      controlsRef.current.update();
    }
  }, [mode, camera, controlsRef, inputs, result, convRatio]);

  return null;
}
